import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

interface CreditsContextValue {
  balance: number | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

const CreditsContext = createContext<CreditsContextValue | undefined>(undefined);

export const CreditsProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setBalance(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from("credit_balances")
      .select("millicredits")
      .eq("user_id", user.id)
      .maybeSingle();
    const mc = (data as { millicredits?: number } | null)?.millicredits ?? 0;
    setBalance(mc / 1000);
    setLoading(false);
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Re-fetch the balance whenever the tab regains focus so users see
  // post-action debits without us broadcasting balances over realtime
  // (which would let other users subscribe to the same channel).
  useEffect(() => {
    if (!user) return;
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [user, refresh]);

  return (
    <CreditsContext.Provider value={{ balance, loading, refresh }}>
      {children}
    </CreditsContext.Provider>
  );
};

export const useCredits = () => {
  const ctx = useContext(CreditsContext);
  if (!ctx) throw new Error("useCredits must be used inside CreditsProvider");
  return ctx;
};